/**
 * @file useNotices.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-11-03
 * @description The full notices list (GET /api/notices) for the Notices
 *              widget and the status bar's alerts window — one loader, so the
 *              two never disagree.
 *
 *   const { data, loading, failed, load, acknowledge, undo, busy, error } = useNotices();
 *
 * Re-read:
 *   - when the shared alert signature moves (useNoticeAlerts — the 20-second
 *     summary poll, or this person's own acknowledge / undo): something
 *     appeared, cleared, or was acknowledged, so the list is re-read at once;
 *   - every five minutes and when the tab comes back into view, as a backstop
 *     (an item's words can change without its key changing).
 *
 * acknowledge(items) / undo(items) mark the rows at once, then save; a refusal
 * puts them back and says why (`error`). items: [{ notice, key }].
 *
 * 1.1.0 (2026-11-03): runAction(notice, item, action) — a row's own action
 * (e.g. "Send to Xero now"): POSTs to the action's url, says the answer
 * (`info`, or `error`), then re-reads the list and the Alerts count.
 * The action names its endpoint as `url` (the held-here alert) or `path` (the
 * variation review's flags) and may carry a `body`; only a path under /api/ is
 * ever sent.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../config';
import { useNoticeAlerts, acknowledgeNotices, unacknowledgeNotices, fetchNoticeSummary } from './useNoticeAlerts';

const REFRESH_MS = 5 * 60 * 1000;
const FOCUS_MIN_GAP_MS = 60 * 1000;

/** The list with these items marked (un)acknowledged and the counts moved. Pure. */
export function markAcknowledged(data, items, acknowledged) {
  if (!data?.notices) return data;
  const wanted = new Set(items.map(i => `${i.notice}\u0000${i.key}`));
  return {
    ...data,
    notices: data.notices.map((n) => {
      let moved = 0;
      const nextItems = (n.items || []).map((it) => {
        if (!wanted.has(`${n.key}\u0000${it.key}`) || !!it.acknowledged === acknowledged) return it;
        moved += 1;
        return { ...it, acknowledged };
      });
      if (!moved) return n;
      const unack = n.unacknowledged == null ? n.unacknowledged
        : Math.max(0, n.unacknowledged + (acknowledged ? -moved : moved));
      return { ...n, items: nextItems, unacknowledged: unack };
    })
  };
}

export default function useNotices() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const lastLoad = useRef(0);
  const inFlight = useRef(null);
  const loadedFor = useRef(null);
  const { signature } = useNoticeAlerts();

  const load = useCallback(async () => {
    if (inFlight.current) inFlight.current.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    lastLoad.current = Date.now();
    try {
      const res = await fetch(`${API_URL}/api/notices`, { credentials: 'include', signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setData(body);
      setFailed(false);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setFailed(true);
    } finally {
      if (inFlight.current === controller) {
        inFlight.current = null;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastLoad.current > FOCUS_MIN_GAP_MS) load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      if (inFlight.current) inFlight.current.abort();
    };
  }, [load]);

  // The live part: the summary's signature moved since the list was read.
  // Keyed on the signature CHANGING (not on it differing from the list's),
  // so two reads a moment apart can never chase each other.
  useEffect(() => {
    if (!signature) return;
    if (loadedFor.current === null) { loadedFor.current = signature; return; }
    if (signature === loadedFor.current) return;
    loadedFor.current = signature;
    load();
  }, [signature, load]);

  const change = useCallback(async (items, acknowledged) => {
    if (!items?.length) return;
    setError(null);
    setBusy(true);
    setData(prev => markAcknowledged(prev, items, acknowledged));
    try {
      await (acknowledged ? acknowledgeNotices(items) : unacknowledgeNotices(items));
    } catch (err) {
      setData(prev => markAcknowledged(prev, items, !acknowledged));
      setError(err.message || 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }, []);

  const acknowledge = useCallback((items) => change(items, true), [change]);
  const undo = useCallback((items) => change(items, false), [change]);

  const runAction = useCallback(async (notice, item, action) => {
    const target = action?.url || action?.path;
    if (typeof target !== 'string' || !target.startsWith('/api/')) return;
    setError(null);
    setInfo(null);
    setActionBusy(true);
    try {
      const res = await fetch(`${API_URL}${target}`, {
        method: String(action.method || 'POST').toUpperCase(),
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action.body || {})
      });
      let body = null;
      try { body = await res.json(); } catch (_) { body = null; }
      if (!res.ok || body?.success === false) throw new Error(body?.error || body?.message || `Could not do that (HTTP ${res.status})`);
      setInfo(body?.message || `${action.label}: done`);
    } catch (err) {
      setError(err.message || 'Could not do that.');
    } finally {
      setActionBusy(false);
      try { await fetchNoticeSummary(); } catch (_) { /* the poll catches up */ }
      await load();
    }
  }, [load]);

  return {
    data, loading, failed, load, acknowledge, undo, busy, error, clearError: () => setError(null),
    runAction, actionBusy, info
  };
}
